'use client';

import {
  WorkspaceMemberRole,
  type WorkspaceRoleListItemDto,
} from '@up2stay/up2promo-api-client';
import { Label } from '@up2stay/up2promo-ui';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@up2stay/up2promo-ui';

import {
  type RoleAssignment,
  roleAssignmentFromValue,
  roleAssignmentToValue,
} from '@/core/authz/role-assignment';

export type RoleSelectDict = {
  label: string;
  placeholder: string;
  systemGroup: string;
  customGroup: string;
  roles: {
    owner: string;
    admin: string;
    member: string;
  };
};

interface RoleSelectProps {
  value: RoleAssignment;
  onChange: (assignment: RoleAssignment) => void;
  roles: WorkspaceRoleListItemDto[];
  currentUserRole: WorkspaceMemberRole;
  dict: RoleSelectDict;
  disabled?: boolean;
  id?: string;
  /** Exclude owner from selectable built-in roles (e.g. invite flow). */
  excludeOwner?: boolean;
}

export function RoleSelect({
  value,
  onChange,
  roles,
  currentUserRole,
  dict,
  disabled,
  id = 'role',
  excludeOwner = false,
}: RoleSelectProps) {
  const customRoles = roles.filter((r) => !r.isSystem);
  const showOwner =
    !excludeOwner && currentUserRole === WorkspaceMemberRole.OWNER;

  const builtinOptions: WorkspaceMemberRole[] = [
    WorkspaceMemberRole.MEMBER,
    WorkspaceMemberRole.ADMIN,
    ...(showOwner ? [WorkspaceMemberRole.OWNER] : []),
  ];

  return (
    <Select
      value={roleAssignmentToValue(value)}
      onValueChange={(next) => {
        const assignment = roleAssignmentFromValue(next);
        if (assignment) onChange(assignment);
      }}
      disabled={disabled}
    >
      <SelectTrigger id={id}>
        <SelectValue placeholder={dict.placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>{dict.systemGroup}</SelectLabel>
          {builtinOptions.map((role) => (
            <SelectItem key={role} value={`builtin:${role}`}>
              {dict.roles[role]}
            </SelectItem>
          ))}
        </SelectGroup>
        {customRoles.length > 0 && (
          <SelectGroup>
            <SelectLabel>{dict.customGroup}</SelectLabel>
            {customRoles.map((role) => (
              <SelectItem key={role.id} value={`custom:${role.id}`}>
                {role.name}
              </SelectItem>
            ))}
          </SelectGroup>
        )}
      </SelectContent>
    </Select>
  );
}

export function RoleSelectField({
  value,
  onChange,
  roles,
  currentUserRole,
  dict,
  disabled,
  id,
  excludeOwner,
}: RoleSelectProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id ?? 'role'}>{dict.label}</Label>
      <RoleSelect
        value={value}
        onChange={onChange}
        roles={roles}
        currentUserRole={currentUserRole}
        dict={dict}
        disabled={disabled}
        id={id}
        excludeOwner={excludeOwner}
      />
    </div>
  );
}
